import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { PageScrollService } from '@app/shared/directives/ngx-page-scroll/ngx-page-scroll-core/providers/ngx-page-scroll.service';
import { Device } from '@app/shared/models/device';
import { TransferFile } from '@app/shared/models/transferFile';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { ViewerActions } from './actions';
import { ModalTransferAccessTrackingComponent } from './modals/modal-transfer-access-tracking/modal-transfer-access-tracking.component';
import { ModalTransferPasswordComponent } from './modals/modal-transfer-password/modal-transfer-password.component';
import * as fromViewer from './reducers/viewer.reducer';
import { GetTransferPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024/types/GetTransferPreview/GetTransferPreview';

@Component({
  selector: 'app-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
})
export class ViewerComponent implements OnInit, OnDestroy {

  device$: Observable<Device>;
  countryCode$: Observable<string>;
  preview$: Observable<GetTransferPreviewOutput>;
  transferMetas$: Observable<any>;
  previewLoading$: Observable<boolean>;
  previewLoadingError$: Observable<any>;
  previewLoaded$: Observable<boolean>;
  previewEnabled$: Observable<boolean>;
  previewIsInQueue$: Observable<boolean>;
  passwordRequired$: Observable<boolean>;
  previewUnlockError$: Observable<any>;
  accessTrackingRequired$: Observable<boolean>;
  files$: Observable<TransferFile[]>;
  selectedFiles$: Observable<TransferFile[]>;
  selectedFilesTotalSize$: Observable<number>;
  urlsToDownload$: Observable<string[]>;
  downloadSectionDisplayed$: Observable<boolean>;
  filesUrlsExpiry$: Observable<string>;
  completeTransferDownloadUrl$: Observable<string>;
  previewModalDisplayed$: Observable<boolean>;
  selectedModalFile$: Observable<TransferFile>;
  isCookiesConsentSetByUser$: Observable<boolean>;
  initialFileOffset$: Observable<number>;
  nextFiles$: Observable<string | null>;
  nextFilesLoading$: Observable<boolean>;
  transferId: string;
  scrolledToPreview = false;
  snackbarDisplayed = false;
  modalPassword: DialogRef;
  subscriptions: Subscription[] = [];
  filesReady = false;
  cookieDisplayed = true;
  hasMoreFiles = false;
  filesLoading = false;

  @ViewChild('iframe') iframe: ElementRef;

  constructor(
    private store$: Store<fromRoot.State & fromViewer.State>,
    private route: ActivatedRoute,
    private pageScrollService: PageScrollService,
    private dialog: DialogService,
    private renderer: Renderer2,

    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: any,

  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.loadTransfer();
      this.preview$ = this.store$.select(fromViewer.getPreview);
      this.transferMetas$ = this.store$.select(fromViewer.getTransferMetas);
      this.device$ = this.store$.select(fromRoot.getDevice);
      this.previewEnabled$ = this.store$.select(fromViewer.getPreviewEnabled);
      this.previewIsInQueue$ = this.store$.select(fromViewer.getPreviewIsInQueue);
      this.previewLoaded$ = this.store$.select(fromViewer.getPreviewLoaded);
      this.previewLoading$ = this.store$.select(fromViewer.getPreviewLoading);
      this.previewLoadingError$ = this.store$.select(fromViewer.getPreviewLoadingError);
      this.previewUnlockError$ = this.store$.select(fromViewer.getPreviewUnlockError);
      // if password OK and accessTracking required
      this.accessTrackingRequired$ = this.store$.select(fromViewer.getAccessTrackingRequired);
      this.accessTrackingRequired$.pipe(first(required => required)).subscribe(async () => await this.openModalAccessTracking());
      this.passwordRequired$ = this.store$.select(fromViewer.getPasswordRequired);
      this.passwordRequired$.pipe(first(required => required)).subscribe(async () => await this.openModalPassword());
      // need to handle 403 basic
      this.files$ = this.store$.select(fromViewer.getFiles);
      this.nextFiles$ = this.store$.select(fromViewer.getNextFile);
      this.nextFilesLoading$ = this.store$.select(fromViewer.getFilesLoading);
      this.urlsToDownload$ = this.store$.select(fromViewer.getUrlsToDownload);
      this.completeTransferDownloadUrl$ = this.store$.select(fromViewer.getCompleteTransferDownloadUrl);
      this.selectedFiles$ = this.store$.select(fromViewer.getSelectedFiles);
      this.selectedFilesTotalSize$ = this.store$.select(fromViewer.getSelectedFilesTotalSize);
      this.initialFileOffset$ = this.store$.select(fromViewer.getSelectedFileOffsetY);
      this.previewModalDisplayed$ = this.store$.select(fromViewer.getModalPreviewDisplayed);
      this.previewModalDisplayed$.subscribe((displayed => { if (!displayed) { this.scrolledToPreview = false; } }));

      this.selectedModalFile$ = this.store$.select(fromViewer.getSelectedModalFile);
      this.isCookiesConsentSetByUser$ = this.store$.select(fromRoot.getIsCookiesConsentSetByUser);
      this.urlsToDownload$.pipe(filter(urls => urls?.length > 0)).subscribe(urls => {
        this.insertDownloadUrlsToDom(urls);
      });

      this.subscriptions.push(
        this.nextFilesLoading$.subscribe(filesLoading => {
          this.filesLoading = filesLoading;
        }),
        this.nextFiles$.subscribe(nextFiles => {
          this.hasMoreFiles = !!(nextFiles);
        }),
        this.files$.subscribe(files => {
          this.filesReady = !!files.length
        }),
        this.isCookiesConsentSetByUser$.subscribe((value) => {
          if (isPlatformBrowser(this.platformId)) {
            if (value) {
              this.cookieDisplayed = false;
              this.renderer.removeClass(this.document.body, 'cookieDisplayed');
            } else {
              this.renderer.addClass(this.document.body, 'cookieDisplayed');
            }
          }
        }),
      )
      this.downloadSectionDisplayed$ = this.store$.select(fromViewer.getDownloadDisplayed);
      this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    }
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.dialog.closeAll();
  }

  loadTransfer(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.store$.dispatch(ViewerActions.LoadPreview({ transferId: id }));
  }

  async openModalPassword() {
    const device = await lastValueFrom(this.device$.pipe(first(deviceType => !!deviceType)));
    this.modalPassword = this.dialog.open(ModalTransferPasswordComponent, {
      closeButton: false,
      enableClose: false,
      size: 'xs',
      data: {
        transferId: this.transferId,
        device
      },
    });
  }

  async openModalAccessTracking() {
    const device = await lastValueFrom(this.device$.pipe(first(deviceType => !!deviceType)));
    if (this.modalPassword) {
      this.modalPassword.close();
    }

    this.dialog.open(ModalTransferAccessTrackingComponent, {
      closeButton: false,
      enableClose: false,
      size: 'xs',
      data: {
        transferId: this.transferId,
        device
      },
    });
  }

  insertDownloadUrlsToDom(urls: string[]): void {
    urls.forEach(url => {
      const iframeToAdd = '<iframe src=\'' + url + '\' style=\'position : absolute; width: 0px; height: 0px;border: none;\'></iframe>\'';
      if (this.iframe) {
        this.iframe.nativeElement.insertAdjacentHTML('beforeend', iframeToAdd);
      }
    });
  }

  download(file?: TransferFile): void {
    this.store$.dispatch(ViewerActions.RequestDownloadUrls({ file }));
    this.store$.dispatch(ViewerActions.DisplayDownloadSection());
  }

  /* Unscroll to top (hide preview component) */
  unscroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrolledToPreview = false;
      this.pageScrollService.scroll({
        document: this.document,
        scrollTarget: '.container-space',
        duration: 250,
      });
    }
  }

  /* Scroll to file preview after modal close */
  scrollToFile({ fileId, fileOffset }) {
    this.pageScrollService.scroll({
      document: this.document,
      scrollOffset: fileOffset,
      scrollTarget: `#${fileId}`,
      duration: 1,
    });
  }

  /* Scroll to preview component */
  scrollToFiles(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrolledToPreview = true;
      this.pageScrollService.scroll({
        document: this.document,
        scrollTarget: '.preview',
        duration: 400,
      });
    }
  }

  /* To Display/Hide preview snackbar */
  @HostListener('window:scroll', ['$event'])
  onScroll() {
    if (window.scrollY > 0) {
      this.snackbarDisplayed = true;
      this.scrolledToPreview = true;
    } else {
      this.snackbarDisplayed = false;
      this.scrolledToPreview = false;
    }
  }

  resolveCoverClickAction(): void {
    if (this.scrolledToPreview) {
      this.unscroll();
    } else {
      this.scrollToFiles();
    }
  }

  nextBatch() {
    if (!this.filesLoading && this.hasMoreFiles) {
      this.store$.dispatch(ViewerActions.LoadMoreFiles());
    }
  }
}
